import { CoreFetch } from '../../api/index';
import { ClaimApi, ListProviderClaimsFilterClaimTypeEnum, ListSupportClaimsFilterClaimTypeEnum } from '../../api/server/apis/ClaimApi';
import { GetWorkerReportRequest, ReportApi } from '../../api/server/apis/ReportApi';
import { ProviderClaim } from '../../api/server/models/ProviderClaim';
import { ProviderWorkerReport } from '../../api/server/models/ProviderWorkerReport';

/**
 * The claim fields the Workflows drawer shows (crosswalk §4). Both consoles' claim models carry every
 * one of them under the same name and type, except two enums, which each console types with its own
 * enum of the same values. Those are strings here.
 */
export type WorkflowClaim = Pick<
    ProviderClaim,
    | 'id'
    | 'patientFirst'
    | 'patientLast'
    | 'patientDob'
    | 'patientExternalId'
    | 'accountNumber'
    | 'primaryPayerName'
    | 'secondaryPayerName'
    | 'primaryPayerSubscriberMemberId'
    | 'primaryThirdPartyAdministratorName'
    | 'fromDos'
    | 'throughDos'
    | 'chargeAmount'
    | 'allowedAmount'
    | 'payerAmount'
    | 'patientAmount'
    | 'type'
    | 'claimResponsibilityType'
    | 'claimAdjudicationDate'
    | 'claimValidationErrors'
    | 'payments'
    | 'paperwork'
    | 'created'
> & {
    claimResponsibilityCategoryType?: string;
    collectionStatus?: string;
};

/**
 * Everything the Workflows page reads, behind one interface (the FE-155 shape): the two consoles read
 * claims from different endpoints, and the page must not know which.
 *
 * | Data | Provider | Support |
 * |---|---|---|
 * | worker report, drill-down IDs | `/provider/report/worker` | the same |
 * | claims for a page of IDs | `/provider/claim?filterId=` | `/support/claim?filterId=` |
 *
 * Measured on sandbox 2026-10-09: a Support token reads `/provider/report/worker` for the account it has
 * switched to, with groups identical to the provider token's. `filterId` with 100 comma-joined `EQ:`
 * values returned all 100 claims on both claim endpoints (~1.9 s provider, ~1.4 s Support).
 */
export interface IWorkerReportApi {
    fetchReport(request: GetWorkerReportRequest): Promise<ProviderWorkerReport>;

    /**
     * The claims for one page of IDs, in any order. An ID the endpoint does not return is simply
     * absent; the caller shows it as unavailable rather than dropping the row.
     */
    fetchClaimsByIds(ids: string[]): Promise<WorkflowClaim[]>;
}

/** One request: `filterId=EQ:a,EQ:b,…` with the page size as the limit. Both consoles OR the values. */
const idFilter = (ids: string[]): { filterId: string[]; limit: number } => ({
    filterId: [ids.map(id => `EQ:${id}`).join(',')],
    limit: ids.length,
});

/** The claim types a page can hold besides PROFESSIONAL, which is what the claim lists return when no type is sent. */
export const OTHER_CLAIM_TYPES = ['INSTITUTIONAL', 'DENTAL', 'NYNF3'] as const;
export type OtherClaimType = (typeof OTHER_CLAIM_TYPES)[number];

/**
 * The claims for a page of IDs, whatever their type (Copilot on #613).
 *
 * The claim lists answer PROFESSIONAL claims unless `filterClaimType` names another type, and they take one
 * type per request. The worker report has every type. So the IDs the first lookup does not return are asked
 * for again, once per other type, in parallel. A page of professional claims, the usual case and all of
 * sandbox (994 of 994 on 2026-10-09), costs no extra request. A failed extra lookup leaves its claims
 * "unavailable" rather than failing the page.
 */
export const lookupAcrossClaimTypes = async (
    ids: string[],
    lookup: (ids: string[], type?: OtherClaimType) => Promise<WorkflowClaim[]>,
): Promise<WorkflowClaim[]> => {
    if (!ids.length) {
        return [];
    }
    const byId = new Map<string, WorkflowClaim>();
    (await lookup(ids)).forEach(claim => claim.id && byId.set(claim.id, claim));
    const missing = ids.filter(id => !byId.has(id));
    if (!missing.length) {
        return [...byId.values()];
    }

    const others = await Promise.allSettled(OTHER_CLAIM_TYPES.map(type => lookup(missing, type)));
    others.forEach(result => {
        if (result.status === 'fulfilled') {
            result.value.forEach(claim => claim.id && !byId.has(claim.id) && byId.set(claim.id, claim));
        }
    });

    return [...byId.values()];
};

const report: Pick<IWorkerReportApi, 'fetchReport'> = {
    fetchReport: request => CoreFetch<ReportApi>(ReportApi).getWorkerReport(request),
};

export const providerWorkerReportApi: IWorkerReportApi = {
    ...report,
    fetchClaimsByIds: ids =>
        lookupAcrossClaimTypes(ids, async (page, type) =>
            (await CoreFetch<ClaimApi>(ClaimApi).listProviderClaims({
                ...idFilter(page),
                filterClaimType: type && ListProviderClaimsFilterClaimTypeEnum[type],
            })) || [],
        ),
};

export const supportWorkerReportApi: IWorkerReportApi = {
    ...report,
    fetchClaimsByIds: ids =>
        lookupAcrossClaimTypes(ids, async (page, type) =>
            (await CoreFetch<ClaimApi>(ClaimApi).listSupportClaims({
                ...idFilter(page),
                filterClaimType: type && ListSupportClaimsFilterClaimTypeEnum[type],
            })) || [],
        ),
};
